/**
 * Cookie Service Utility
 * Provides methods for reading, writing, and managing browser cookies
 */

export class CookieService {
    /**
     * Set a cookie with specified name, value, and options
     * @param {string} name - Cookie name
     * @param {string} value - Cookie value
     * @param {Object} options - Cookie options
     * @param {number} options.days - Days until expiration
     * @param {string} options.path - Cookie path (default: '/')
     * @param {string} options.domain - Cookie domain
     * @param {boolean} options.secure - Secure flag
     * @param {string} options.sameSite - SameSite attribute ('Strict', 'Lax', 'None')
     */
    static setCookie(name, value, options = {}) {
        const {
            days = 365,
            path = '/',
            domain,
            secure = false,
            sameSite = 'Lax'
        } = options;

        let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            cookieString += `; expires=${date.toUTCString()}`;
        }

        cookieString += `; path=${path}`;

        if (domain) {
            cookieString += `; domain=${domain}`;
        }

        if (secure) {
            cookieString += '; secure';
        }

        cookieString += `; samesite=${sameSite}`;

        document.cookie = cookieString;
    }

    /**
     * Get a cookie value by name
     * @param {string} name - Cookie name
     * @returns {string|null} - Cookie value or null if not found
     */
    static getCookie(name) {
        const nameEQ = `${encodeURIComponent(name)}=`;
        const cookies = document.cookie.split(';');

        for (let cookie of cookies) {
            cookie = cookie.trim();
            if (cookie.indexOf(nameEQ) === 0) {
                return decodeURIComponent(cookie.substring(nameEQ.length));
            }
        }
        return null;
    }

    /**
     * Delete a cookie by name
     * @param {string} name - Cookie name
     * @param {string} path - Cookie path (default: '/')
     * @param {string} domain - Cookie domain
     */
    static deleteCookie(name, path = '/', domain) {
        let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
        
        if (domain) {
            cookieString += `; domain=${domain}`;
        }

        document.cookie = cookieString;
    }

    /**
     * Check if a cookie exists
     * @param {string} name - Cookie name
     * @returns {boolean} - True if cookie exists
     */
    static hasCookie(name) {
        return CookieService.getCookie(name) !== null;
    }

    /**
     * Get all cookies as an object
     * @returns {Object} - Object with cookie names as keys and values as values
     */
    static getAllCookies() {
        const cookies = {};
        const cookieArray = document.cookie.split(';');

        for (let cookie of cookieArray) {
            cookie = cookie.trim();
            if (cookie) {
                const [name, value] = cookie.split('=');
                if (name && value) {
                    cookies[decodeURIComponent(name)] = decodeURIComponent(value);
                }
            }
        }

        return cookies;
    }

    /**
     * Clear all cookies (attempts to delete all readable cookies)
     * @param {string} path - Cookie path (default: '/')
     * @param {string} domain - Cookie domain
     */
    static clearAllCookies(path = '/', domain) {
        const cookies = CookieService.getAllCookies();
        
        for (const cookieName in cookies) {
            CookieService.deleteCookie(cookieName, path, domain);
        }
    }

    /**
     * Set a JSON object as a cookie
     * @param {string} name - Cookie name
     * @param {Object} obj - Object to store
     * @param {Object} options - Cookie options
     */
    static setObjectCookie(name, obj, options = {}) {
        const jsonString = JSON.stringify(obj);
        CookieService.setCookie(name, jsonString, options);
    }

    /**
     * Get a JSON object from a cookie
     * @param {string} name - Cookie name
     * @returns {Object|null} - Parsed object or null if not found/invalid
     */
    static getObjectCookie(name) {
        const cookieValue = CookieService.getCookie(name);
        if (!cookieValue) return null;

        try {
            return JSON.parse(cookieValue);
        } catch (error) {
            console.warn(`Failed to parse cookie "${name}" as JSON:`, error);
            return null;
        }
    }
}

// Default export for convenience
export default CookieService;

// Named exports for individual methods (if preferred)
export const {
    setCookie,
    getCookie,
    deleteCookie,
    hasCookie,
    getAllCookies,
    clearAllCookies,
    setObjectCookie,
    getObjectCookie
} = CookieService;